import { diaNegocio, formatearMoneda, hora12, nombreServicios, serviciosDe, type ArqueoInput, type CitaVista, type TurnoCajaVista, type VentaVista } from '@barbones/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowDownToLine, CalendarClock, Clock, Info, PiggyBank, KeyRound, Lock, LockOpen, Plus, Printer, Receipt, ShoppingBag, Wallet } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Indicador, Insignia, Pestana, PestanaContenido, Pestanas, PestanasLista, Tarjeta, Vacio } from '@/components/ui/data';
import { Campo, Input, Select } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { cn, fechaHora } from '@/lib/utils';
import { DialogoIngresoCierre } from '../boveda/DialogoIngresoCierre';
import { ArqueoForm, contadoDe } from './ArqueoForm';
import { Carrito } from './Carrito';
import { useAgendaDia } from '../agenda/datos';
import { avisarErrorCaja, useDenominaciones, useEnServicio, usePorCobrar, useRefrescarCaja, useSuspendidas, useTurnoActual } from './datos';
import { imprimirTicket } from './imprimir';
import { NuevaVentaMostradorDialog } from './NuevaVentaMostradorDialog';
import { PanelPago } from './PanelPago';
import { DetalleVentaDialogo, VentasDelDia } from './VentasDelDia';
import { avisarError } from '@/lib/avisos';

type Operacion = 'corte' | 'cierre' | 'movimiento' | 'fondo' | null;

/** Búsqueda de `/caja`: los avisos enlazan a una venta (`?venta=`) y, si aplica, a su reembolso. */
export interface BusquedaCaja {
  venta?: string;
  reembolso?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Solo ids con forma de UUID: la URL de un aviso nunca arma otra ruta de la API. */
const idValido = (v: unknown): string | undefined => (typeof v === 'string' && UUID.test(v) ? v : undefined);

/**
 * Venta indicada por `?venta=`: si se puede cobrar (por cobrar o en servicio) se selecciona y se
 * limpia la URL; si ya se cobró o canceló, se devuelve para mostrar su detalle (con el reembolso).
 */
function useVentaDeLaUrl(onSeleccionar: (id: string) => void) {
  const busqueda = useSearch({ strict: false }) as BusquedaCaja;
  const navegar = useNavigate();
  const ventaId = idValido(busqueda.venta);
  const venta = useQuery({
    queryKey: ['ventas', 'detalle', ventaId],
    queryFn: () => api<VentaVista>(`/ventas/${ventaId}`),
    enabled: Boolean(ventaId),
    retry: false,
  });
  const limpiar = () => void navegar({ to: '/caja', search: {}, replace: true });
  const cobrable = venta.data?.estado === 'por_cobrar' || venta.data?.estado === 'abierta';
  useEffect(() => {
    if (!ventaId || !venta.data || venta.data.id !== ventaId || !cobrable) return;
    onSeleccionar(ventaId);
    limpiar();
  }, [ventaId, venta.data?.id, cobrable]);
  useEffect(() => {
    if (!ventaId || !venta.isError) return;
    avisarError(venta.error);
    limpiar();
  }, [ventaId, venta.isError]);
  const detalle = ventaId && venta.data?.id === ventaId && !cobrable ? venta.data : null;
  return { detalle, reembolso: busqueda.reembolso, cerrar: limpiar };
}


function useOperacionCaja(ruta: string, mensaje: string, alTerminar?: (t: TurnoCajaVista) => void) {
  const refrescar = useRefrescarCaja();
  return useMutation({
    mutationFn: (body: object) => api<TurnoCajaVista>(`/caja/${ruta}`, { method: 'POST', body }),
    onSuccess: (t) => {
      if (t.estado === 'bloqueado') toast.warning('Hay un faltante: la caja quedó bloqueada hasta reponer o autorizar.');
      else toast.success(mensaje);
      refrescar();
      alTerminar?.(t);
    },
    onError: (e) => avisarErrorCaja(e),
  });
}

function Apertura({ fondo }: { fondo: { MXN: number; USD: number } }) {
  const den = useDenominaciones();
  const abrir = useOperacionCaja('abrir', 'Caja abierta');
  const fondoDejado = [fondo.MXN ? formatearMoneda(fondo.MXN) : null, fondo.USD ? formatearMoneda(fondo.USD, 'USD') : null].filter(Boolean).join(' y ');
  return (
    <Tarjeta className="mx-auto max-w-3xl p-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="rounded-full bg-accent-soft p-3 text-accent">
          <LockOpen className="size-5" />
        </div>
        <div>
          <h2 className="text-[22px]">Abrir caja</h2>
          <p className="text-[13px] text-foreground-muted">Cuenta el fondo por denominación o captura el total. El sistema lo compara con el fondo que se dejó al cerrar.</p>
        </div>
      </div>
      {fondoDejado && (
        <p role="alert" className="mb-5 flex items-center gap-2 rounded-lg bg-info-soft px-4 py-3 text-[14px] font-medium text-info">
          <Info className="size-5 shrink-0" aria-hidden />
          El día de ayer se dejó un fondo de {fondoDejado} para el día de hoy, por favor cuenta el efectivo.
        </p>
      )}
      <ArqueoForm
        denominacionesMXN={den.MXN}
        denominacionesUSD={den.USD}
        esperadoMXN={fondo.MXN}
        esperadoUSD={fondo.USD}
        textoConfirmar="Abrir caja"
        cargando={abrir.isPending}
        onConfirmar={(c) => abrir.mutate(c)}
      />
    </Tarjeta>
  );
}

function Bloqueo({ turno }: { turno: TurnoCajaVista }) {
  const { puede } = useSesion();
  const den = useDenominaciones();
  const refrescar = useRefrescarCaja();
  const arqueo = turno.arqueoPendiente!;
  const [modo, setModo] = useState<'reponer' | 'dueno' | null>(null);
  const [cred, setCred] = useState({ usuario: '', password: '' });
  const reponer = useOperacionCaja('reponer', 'Faltante repuesto; caja desbloqueada', () => setModo(null));

  const solicitar = useMutation({
    mutationFn: () => api('/caja/desbloquear', { method: 'POST', body: { arqueoId: arqueo.id, solicitarAlDueno: true } }),
    onSuccess: () => {
      toast.success('Solicitud enviada al dueño. La caja se desbloqueará en cuanto la apruebe.');
      setModo(null);
    },
    onError: (e) => avisarError(e),
  });
  const desbloquear = useMutation({
    mutationFn: async () => {
      const autorizacionToken = puede('caja.desbloquear')
        ? undefined
        : (
            await api<{ autorizacionToken: string }>('/auth/verificar-dueno', {
              method: 'POST',
              body: { ...cred, motivo: `Desbloquear ${arqueo.tipo} con faltante`, accion: 'desbloqueo_caja' },
            })
          ).autorizacionToken;
      return api('/caja/desbloquear', { method: 'POST', body: { arqueoId: arqueo.id, autorizacionToken } });
    },
    onSuccess: () => {
      toast.success('Caja desbloqueada por el dueño. El faltante queda para el pago semanal.');
      refrescar();
      setModo(null);
    },
    onError: (e) => toast.error(e instanceof ErrorApi && e.status === 401 ? 'Usuario o contraseña del dueño incorrectos' : mensajeError(e)),
  });

  const falta = [arqueo.diferenciaMXN < 0 ? formatearMoneda(-arqueo.diferenciaMXN) : null, arqueo.diferenciaUSD < 0 ? formatearMoneda(-arqueo.diferenciaUSD, 'USD') : null]
    .filter(Boolean)
    .join(' y ');

  return (
    <Tarjeta className="mx-auto max-w-2xl border-destructive/40 p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-destructive-soft p-3 text-destructive">
          <Lock className="size-5" />
        </div>
        <div>
          <h2 className="text-[22px]">Caja bloqueada</h2>
          <p className="text-[14px]">
            En el {arqueo.tipo} de las {hora12(new Date(arqueo.fecha))} faltaron <strong className="cifras">{falta}</strong> (contó {arqueo.realizadoPor}).
          </p>
          <p className="mt-1 text-[13px] text-foreground-muted">Repón el efectivo y vuelve a contar, o pide al dueño que autorice continuar.</p>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => setModo('reponer')}>
          <Wallet /> Reponer y contar de nuevo
        </Button>
        <Button variante="secundario" onClick={() => setModo('dueno')}>
          <KeyRound /> Autorización del dueño
        </Button>
      </div>

      {modo === 'reponer' && (
        <Dialogo abierto ancho="lg" onCambiarAbierto={(v) => !v && setModo(null)} titulo="Reponer faltante">
          <ArqueoForm
            denominacionesMXN={den.MXN}
            denominacionesUSD={den.USD}
            esperadoMXN={arqueo.esperadoMXN}
            esperadoUSD={arqueo.esperadoUSD}
            textoConfirmar="Registrar reposición"
            cargando={reponer.isPending}
            onConfirmar={(c) => reponer.mutate({ ...c, arqueoId: arqueo.id })}
          />
        </Dialogo>
      )}
      {modo === 'dueno' && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={(v) => !v && setModo(null)}
          titulo="Autorización del dueño"
          descripcion="El faltante quedará a cargo del responsable para descontarse o perdonarse en su pago semanal."
          pie={
            <>
              {!puede('caja.desbloquear') && (
                <Button variante="secundario" onClick={() => solicitar.mutate()} cargando={solicitar.isPending}>
                  Enviar al dueño
                </Button>
              )}
              <Button onClick={() => desbloquear.mutate()} cargando={desbloquear.isPending} disabled={!puede('caja.desbloquear') && (!cred.usuario || !cred.password)}>
                Desbloquear caja
              </Button>
            </>
          }
        >
          {puede('caja.desbloquear') ? (
            <p className="text-[14px]">Como dueño, puedes autorizar directamente.</p>
          ) : (
            <div className="grid gap-3">
              <Campo etiqueta="Usuario del dueño">{(p) => <Input {...p} value={cred.usuario} onChange={(e) => setCred({ ...cred, usuario: e.target.value })} autoComplete="off" />}</Campo>
              <Campo etiqueta="Contraseña">{(p) => <Input {...p} type="password" value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} autoComplete="off" />}</Campo>
            </div>
          )}
        </Dialogo>
      )}
    </Tarjeta>
  );
}

/**
 * Agregar fondo: efectivo que entra al cajón (cambio, fondo olvidado al abrir…). Se registra como
 * ingreso del turno, así que suma al efectivo esperado del corte y del cierre.
 */
function DialogoFondo({ onCerrar }: { onCerrar: () => void }) {
  const [d, setD] = useState({ monto: '', moneda: 'MXN', nota: '' });
  const guardar = useOperacionCaja('movimientos', 'Fondo agregado a la caja', onCerrar);
  const monto = Math.round((Number.parseFloat(d.monto) || 0) * 100);
  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo="Agregar fondo a la caja"
      descripcion="Efectivo que se mete al cajón. Se suma a lo esperado en el corte y en el cierre."
      pie={
        <Button
          cargando={guardar.isPending}
          disabled={monto <= 0}
          onClick={() =>
            guardar.mutate({ tipo: 'ingreso', moneda: d.moneda, monto, concepto: d.nota.trim() ? `Fondo de caja: ${d.nota.trim()}` : 'Fondo de caja' })
          }
        >
          Agregar fondo
        </Button>
      }
    >
      <div className="grid gap-3">
        <div className="grid grid-cols-[1fr_100px] gap-2">
          <Campo etiqueta="Monto" requerido>
            {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" autoFocus value={d.monto} onChange={(e) => setD({ ...d, monto: e.target.value })} className="cifras" />}
          </Campo>
          <Campo etiqueta="Moneda">
            {(p) => (
              <Select {...p} value={d.moneda} onChange={(e) => setD({ ...d, moneda: e.target.value })}>
                <option>MXN</option>
                <option>USD</option>
              </Select>
            )}
          </Campo>
        </div>
        <Campo etiqueta="Nota (opcional)" ayuda="Por ejemplo: «cambio para el día» o «fondo que no se registró al abrir».">
          {(p) => <Input {...p} maxLength={150} value={d.nota} onChange={(e) => setD({ ...d, nota: e.target.value })} />}
        </Campo>
      </div>
    </Dialogo>
  );
}

/**
 * Segundo paso del cierre: tras contar la caja se pregunta, aparte, cuánto efectivo se deja como
 * fondo para la siguiente apertura. El servidor rechaza un fondo mayor a lo contado; aquí se avisa antes.
 */
function DialogoFondoCierre({ contadoMXN, cargando, onConfirmar, onAtras, onCerrar }: { contadoMXN: number; cargando: boolean; onConfirmar: (fondoMXN: number) => void; onAtras: () => void; onCerrar: () => void }) {
  const [fondo, setFondo] = useState('');
  const fondoMXN = Math.round((Number.parseFloat(fondo) || 0) * 100);
  const excede = fondoMXN > contadoMXN;
  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo="Ingresar fondo para la apertura de mañana (opcional)"
      descripcion="Efectivo que se queda en la caja para empezar mañana. El resto se retira al cerrar."
      pie={
        <>
          <Button variante="secundario" onClick={onAtras} disabled={cargando}>
            Atrás
          </Button>
          <Button cargando={cargando} disabled={excede} onClick={() => onConfirmar(fondoMXN)}>
            Cerrar caja
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Campo
          etiqueta="Fondo para mañana (MXN)"
          error={excede ? 'El fondo no puede ser mayor a lo contado.' : undefined}
          ayuda="Déjalo vacío si no se deja fondo."
        >
          {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" autoFocus value={fondo} onChange={(e) => setFondo(e.target.value)} className="cifras w-48" />}
        </Campo>
        <dl className="cifras grid grid-cols-[1fr_auto] gap-y-1 rounded-md bg-muted px-3 py-2 text-[13px]">
          <dt className="text-foreground-muted">Contado en pesos</dt>
          <dd className="text-right">{formatearMoneda(contadoMXN)}</dd>
          <dt className="text-foreground-muted">Se retira</dt>
          <dd className="text-right font-medium">{formatearMoneda(Math.max(contadoMXN - fondoMXN, 0))}</dd>
        </dl>
      </div>
    </Dialogo>
  );
}

function DialogoMovimiento({ onCerrar }: { onCerrar: () => void }) {
  const [d, setD] = useState({ tipo: 'retiro', monto: '', concepto: '', moneda: 'MXN' });
  const guardar = useOperacionCaja('movimientos', 'Movimiento registrado', onCerrar);
  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo="Movimiento de efectivo"
      descripcion="Retiros o ingresos de efectivo. Los gastos se pagan desde la Bóveda."
      pie={
        <Button
          cargando={guardar.isPending}
          disabled={!d.monto || !d.concepto}
          onClick={() => guardar.mutate({ tipo: d.tipo, moneda: d.moneda, monto: Math.round(Number.parseFloat(d.monto) * 100), concepto: d.concepto })}
        >
          Registrar
        </Button>
      }
    >
      <div className="grid gap-3">
        <Campo etiqueta="Tipo">
          {(p) => (
            <Select {...p} value={d.tipo} onChange={(e) => setD({ ...d, tipo: e.target.value })}>
              <option value="retiro">Retiro de efectivo</option>
              <option value="ingreso">Ingreso de efectivo</option>
            </Select>
          )}
        </Campo>
        <div className="grid grid-cols-[1fr_100px] gap-2">
          <Campo etiqueta="Monto">{(p) => <Input {...p} type="number" min="0" step="0.01" value={d.monto} onChange={(e) => setD({ ...d, monto: e.target.value })} className="cifras" />}</Campo>
          <Campo etiqueta="Moneda">
            {(p) => (
              <Select {...p} value={d.moneda} onChange={(e) => setD({ ...d, moneda: e.target.value })}>
                <option>MXN</option>
                <option>USD</option>
              </Select>
            )}
          </Campo>
        </div>
        <Campo etiqueta="Concepto">{(p) => <Input {...p} value={d.concepto} onChange={(e) => setD({ ...d, concepto: e.target.value })} />}</Campo>
      </div>
    </Dialogo>
  );
}

function ListaVentas({ ventas, seleccion, onSeleccionar, vacio }: { ventas: VentaVista[]; seleccion?: string; onSeleccionar: (v: VentaVista) => void; vacio: string }) {
  if (ventas.length === 0) return <p className="px-3 py-8 text-center text-[13px] text-foreground-muted">{vacio}</p>;
  return (
    <ul className="flex flex-col gap-1 p-2">
      {ventas.map((v) => (
        <li key={v.id}>
          <button
            type="button"
            onClick={() => onSeleccionar(v)}
            aria-pressed={v.id === seleccion}
            className={cn('w-full rounded-lg border px-3 py-2.5 text-left transition-colors', v.id === seleccion ? 'border-accent bg-accent-soft' : 'border-transparent hover:bg-muted')}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[13.5px] font-medium">{v.cliente?.nombre ?? 'Mostrador'}</span>
              <span className="cifras text-[13.5px] font-semibold">{formatearMoneda(v.total)}</span>
            </div>
            <p className="truncate text-[12px] text-foreground-muted">
              {v.barbero?.nombre ?? '—'} · {v.lineas.map((l) => l.descripcion).join(', ') || 'Sin conceptos'}
            </p>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Clientes de hoy que aún no pasan a la silla (a partir de la hora actual). Su carrito se abre al iniciar el servicio. */
export function ListaProximas({ citas }: { citas: CitaVista[] }) {
  return (
    <ul className="flex flex-col gap-1 p-2">
      {citas.map((c) => (
        <li key={c.id} className="rounded-lg border border-dashed border-border px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[13.5px] font-medium">{c.cliente.nombre}</span>
            <span className="cifras text-[13px] text-foreground-muted">{formatearMoneda(serviciosDe(c).reduce((suma, s) => suma + s.precio, 0))}</span>
          </div>
          <p className="truncate text-[12px] text-foreground-muted">
            <span className="cifras">{hora12(new Date(c.inicio))}</span> · {c.barbero ?? 'Sin asignar'} · {nombreServicios(serviciosDe(c))}
          </p>
        </li>
      ))}
    </ul>
  );
}

function Seccion({ titulo, cantidad, children }: { titulo: string; cantidad: number; children: ReactNode }) {
  return (
    <section aria-label={titulo}>
      <h3 className="flex items-center gap-2 px-3 pt-3 text-[11.5px] font-medium uppercase tracking-wide text-foreground-muted">
        {titulo} <span className="cifras">{cantidad}</span>
      </h3>
      {children}
    </section>
  );
}

export function CajaPage() {
  const { puede } = useSesion();
  const turnoQ = useTurnoActual();
  const porCobrar = usePorCobrar();
  const enServicio = useEnServicio();
  const agendaHoy = useAgendaDia(diaNegocio(new Date()));
  const ahora = Date.now();
  const proximas = (agendaHoy.data?.citas ?? [])
    .filter((c) => (c.estado === 'agendada' || c.estado === 'en_tolerancia') && new Date(c.fin).getTime() >= ahora)
    .sort((a, b) => a.inicio.localeCompare(b.inicio));
  const suspendidas = useSuspendidas();
  const den = useDenominaciones();
  const refrescar = useRefrescarCaja();
  const [seleccionId, setSeleccionId] = useState<string | undefined>();
  const desdeUrl = useVentaDeLaUrl(setSeleccionId);
  const [operacion, setOperacion] = useState<Operacion>(null);
  const [retiro, setRetiro] = useState('');
  /** Cierre en dos pasos: primero el conteo; con el conteo capturado se pide aparte el fondo para mañana. */
  const [conteoCierre, setConteoCierre] = useState<ArqueoInput | null>(null);
  const [pasoFondo, setPasoFondo] = useState(false);
  /** Turno recién cerrado: se ofrece ingresar dinero a la bóveda. */
  const [ingresoCierre, setIngresoCierre] = useState<string | null>(null);
  const ventasDia = useQuery({
    queryKey: ['ventas', 'del-dia'],
    queryFn: () => api<VentaVista[]>('/ventas/del-dia', { query: { dia: new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date()) } }),
  });

  const corte = useOperacionCaja('corte', 'Corte registrado', () => setOperacion(null));
  const cancelarCierre = () => {
    setConteoCierre(null);
    setPasoFondo(false);
    setOperacion(null);
  };
  const cierre = useOperacionCaja('cerrar', 'Caja cerrada', (t) => {
    cancelarCierre();
    if (t.estado === 'cerrado' && puede('boveda.ingresar')) setIngresoCierre(t.id);
  });
  const [nuevaMostrador, setNuevaMostrador] = useState(false);
  const retomar = useMutation({
    mutationFn: (id: string) => api<VentaVista>(`/ventas/${id}/retomar`, { method: 'POST' }),
    onSuccess: (v) => {
      refrescar();
      setSeleccionId(v.id);
    },
    onError: (e) => avisarError(e),
  });

  const turno = turnoQ.data?.turno;
  const seleccion = [...(porCobrar.data ?? []), ...(enServicio.data ?? [])].find((v) => v.id === seleccionId);

  if (turnoQ.isLoading) return <ModuleLayout titulo="Caja" secciones={SECCIONES_POR_MODULO.caja}><Esqueleto className="h-96" /></ModuleLayout>;

  return (
    <ModuleLayout titulo="Caja" secciones={SECCIONES_POR_MODULO.caja}>
      <EncabezadoPagina
        titulo="Caja"
        descripcion={turno ? `Turno abierto por ${turno.responsable.nombre} · ${fechaHora(turno.abiertoEn)}` : 'La caja está cerrada'}
        acciones={
          turno?.estado === 'abierto' && (
            <>
              <Button variante="secundario" onClick={() => setOperacion('fondo')}>
                <PiggyBank /> Agregar fondo
              </Button>
              <Button variante="secundario" onClick={() => setOperacion('movimiento')}>
                <ArrowDownToLine /> Movimiento
              </Button>
              <Button variante="secundario" onClick={() => setOperacion('corte')}>
                <Receipt /> Corte
              </Button>
              <Button onClick={() => setOperacion('cierre')}>
                <Lock /> Cerrar caja
              </Button>
            </>
          )
        }
      />

      {!turno ? (
        <Apertura fondo={turnoQ.data?.fondoSugerido ?? { MXN: 0, USD: 0 }} />
      ) : turno.estado === 'bloqueado' ? (
        <Bloqueo turno={turno} />
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Indicador etiqueta="Efectivo esperado" valor={formatearMoneda(turno.esperadoMXN)} detalle={turno.esperadoUSD ? `+ ${formatearMoneda(turno.esperadoUSD, 'USD')}` : 'Pesos en caja'} icono={<Wallet />} />
            <Indicador etiqueta="Vendido en el turno" valor={formatearMoneda(turno.totalVendidoMXN)} detalle={`${turno.ventasPagadas} ${turno.ventasPagadas === 1 ? 'venta' : 'ventas'}`} icono={<ShoppingBag />} />
            <Indicador etiqueta="Transferencias" valor={formatearMoneda(turno.transferenciasMXN)} icono={<Receipt />} />
            <Indicador etiqueta="Tarjeta" valor={formatearMoneda(turno.tarjeta.brutoMXN)} detalle={`Comisión ${formatearMoneda(turno.tarjeta.comisionMXN)} · neto ${formatearMoneda(turno.tarjeta.netoMXN)}`} icono={<Receipt />} />
            <Indicador etiqueta="Por cobrar" valor={porCobrar.data?.length ?? 0} detalle={`${suspendidas.data?.length ?? 0} suspendidas`} icono={<Clock />} />
          </div>

          <div className="grid gap-4 lg:grid-cols-[300px_1fr_360px]">
            <Tarjeta className="flex max-h-[70vh] flex-col overflow-hidden">
              <Pestanas defaultValue="cobrar" className="flex min-h-0 flex-1 flex-col">
                <PestanasLista className="px-2">
                  <Pestana value="cobrar">Clientes {porCobrar.data?.length ? <Insignia tono="acento">{porCobrar.data.length}</Insignia> : null}</Pestana>
                  <Pestana value="suspendidas">Suspendidas</Pestana>
                  <Pestana value="dia">Del día</Pestana>
                </PestanasLista>
                <PestanaContenido value="cobrar" className="min-h-0 flex-1 overflow-y-auto">
                  <Seccion titulo="Por cobrar" cantidad={porCobrar.data?.length ?? 0}>
                    <ListaVentas ventas={porCobrar.data ?? []} seleccion={seleccionId} onSeleccionar={(v) => setSeleccionId(v.id)} vacio="Nada por cobrar todavía." />
                  </Seccion>
                  <Seccion titulo="En servicio" cantidad={enServicio.data?.length ?? 0}>
                    <ListaVentas ventas={enServicio.data ?? []} seleccion={seleccionId} onSeleccionar={(v) => setSeleccionId(v.id)} vacio="Nadie en la silla ahora." />
                  </Seccion>
                  {proximas.length > 0 && (
                    <Seccion titulo="Próximos de hoy" cantidad={proximas.length}>
                      <ListaProximas citas={proximas} />
                    </Seccion>
                  )}
                </PestanaContenido>
                <PestanaContenido value="suspendidas" className="min-h-0 flex-1 overflow-y-auto">
                  <ListaVentas ventas={suspendidas.data ?? []} onSeleccionar={(v) => retomar.mutate(v.id)} vacio="Sin ventas suspendidas" />
                  {(suspendidas.data?.length ?? 0) > 0 && (
                    <p className="px-3 pb-3 text-[12px] text-foreground-muted">Al cerrar la caja se cancelarán con el motivo «Cierre de caja».</p>
                  )}
                </PestanaContenido>
                <PestanaContenido value="dia" className="min-h-0 flex-1 overflow-y-auto">
                  <VentasDelDia ventas={ventasDia.data ?? []} puedeCorregir={puede('ventas.corregir_cobros')} puedeReintentar={puede('aprobaciones.resolver')} />
                </PestanaContenido>
              </Pestanas>
              <div className="border-t border-border p-2">
                <Button variante="secundario" className="w-full" onClick={() => setNuevaMostrador(true)}>
                  <Plus /> Venta de mostrador
                </Button>
              </div>
            </Tarjeta>

            <Tarjeta className="min-h-[420px] overflow-hidden">
              {seleccion ? (
                <Carrito venta={seleccion} onCerrarVenta={() => setSeleccionId(undefined)} />
              ) : (
                <Vacio icono={<ShoppingBag />} titulo="Selecciona una venta" descripcion="Elige un cliente de la lista para ver qué se le va a cobrar, o inicia una venta de mostrador." />
              )}
            </Tarjeta>

            <Tarjeta className="p-4">
              {seleccion?.estado === 'abierta' ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center text-[13px] text-foreground-muted">
                  <p>El servicio sigue en curso. Se cobra cuando se finalice desde la agenda o desde la app del barbero.</p>
                  {seleccion.citaId && (
                    <Link to="/agenda" search={{ cita: seleccion.citaId }} className="inline-flex items-center gap-1.5 text-accent hover:underline">
                      <CalendarClock className="size-4" aria-hidden /> Ver la cita en la agenda
                    </Link>
                  )}
                </div>
              ) : seleccion && seleccion.lineas.length > 0 ? (
                <PanelPago key={seleccion.id} venta={seleccion} bloqueado={false} onCobrada={() => setSeleccionId(undefined)} />
              ) : (
                <p className="py-10 text-center text-[13px] text-foreground-muted">El cobro aparece al seleccionar una venta con conceptos.</p>
              )}
            </Tarjeta>
          </div>
        </>
      )}

      {operacion === 'corte' && turno && (
        <Dialogo abierto ancho="lg" onCambiarAbierto={(v) => !v && setOperacion(null)} titulo="Corte de caja" descripcion="Cuenta el efectivo por denominación o captura el total.">
          <ArqueoForm
            denominacionesMXN={den.MXN}
            denominacionesUSD={den.USD}
            esperadoMXN={turno.esperadoMXN}
            esperadoUSD={turno.esperadoUSD}
            textoConfirmar="Registrar corte"
            cargando={corte.isPending}
            onConfirmar={(c) => corte.mutate({ ...c, retiroMXN: Math.round((Number.parseFloat(retiro) || 0) * 100) })}
            extra={
              <Campo etiqueta="Retirar efectivo (opcional)" ayuda="Se entrega al dueño y sale del esperado de caja.">
                {(p) => <Input {...p} type="number" min="0" step="0.01" value={retiro} onChange={(e) => setRetiro(e.target.value)} className="cifras w-48" />}
              </Campo>
            }
          />
        </Dialogo>
      )}
      {operacion === 'cierre' && turno && !pasoFondo && (
        <Dialogo abierto ancho="lg" onCambiarAbierto={(v) => !v && cancelarCierre()} titulo="Cerrar caja" descripcion="Cuenta el efectivo por denominación o captura el total. Después podrás indicar el fondo para mañana.">
          {(suspendidas.data?.length ?? 0) > 0 && (
            <p className="mb-4 rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
              Hay {suspendidas.data!.length} venta(s) suspendida(s): se cancelarán con el motivo «Cierre de caja».
            </p>
          )}
          <ArqueoForm
            denominacionesMXN={den.MXN}
            denominacionesUSD={den.USD}
            esperadoMXN={turno.esperadoMXN}
            esperadoUSD={turno.esperadoUSD}
            textoConfirmar="Continuar"
            inicial={conteoCierre ?? undefined}
            onConfirmar={(c) => {
              setConteoCierre(c);
              setPasoFondo(true);
            }}
          />
        </Dialogo>
      )}
      {operacion === 'cierre' && turno && pasoFondo && conteoCierre && (
        <DialogoFondoCierre
          contadoMXN={contadoDe(conteoCierre).MXN}
          cargando={cierre.isPending}
          onConfirmar={(fondoSiguienteMXN) => cierre.mutate({ ...conteoCierre, fondoSiguienteMXN })}
          onAtras={() => setPasoFondo(false)}
          onCerrar={cancelarCierre}
        />
      )}
      {nuevaMostrador && (
        <NuevaVentaMostradorDialog
          onCerrar={() => setNuevaMostrador(false)}
          onCreada={(v) => {
            setNuevaMostrador(false);
            refrescar();
            setSeleccionId(v.id);
          }}
        />
      )}
      {ingresoCierre && <DialogoIngresoCierre turnoId={ingresoCierre} conUSD={den.USD.length > 0} onTerminar={() => setIngresoCierre(null)} />}
      {desdeUrl.detalle && (
        <DetalleVentaDialogo
          venta={desdeUrl.detalle}
          puedeCorregir={false}
          puedeReintentar={puede('aprobaciones.resolver')}
          reembolsoResaltado={desdeUrl.reembolso}
          onCerrar={desdeUrl.cerrar}
        />
      )}
      {operacion === 'movimiento' && <DialogoMovimiento onCerrar={() => setOperacion(null)} />}
      {operacion === 'fondo' && <DialogoFondo onCerrar={() => setOperacion(null)} />}
    </ModuleLayout>
  );
}
